import { ReactElement } from "react";
import {
	DateString,
	Icon,
	Space,
	Highlight,
	HighlightedText,
	IconAndText,
	PipeSeparator
} from "client/Components";
import { GlobalSearchScopeIdentifier, KnowledgeItemGlobalSearchResultItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { useHighlights } from "../../Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { ItemLayout } from "./ItemLayout";
import { hubItemKindToIcon, HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { localize } from "client_legacy/ndaw/culture";
import { NumberFormatter, useKeyboardListNavigationItem } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useKnowledgeItemNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useKnowledgeItemNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";

export type KnowledgeItemViewProps = {
	item: KnowledgeItemGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	isMobile: boolean;
	timezone: TimeZone;
};

export const KnowledgeItemView = (props: KnowledgeItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		isMobile,
		timezone
	} = props;

	const id: number = item.knowledgeItem
		.id;

	const executeHandler: () => void = useKnowledgeItemNavigationHandler({
		id,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("knowledge-item", `|${id}`)}
		>
			<KnowledgeItemItem
				item={item}
				isMobile={isMobile}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
KnowledgeItemView.displayName = "KnowledgeItemView";

type KnowledgeItemItemProps = {
	item: KnowledgeItemGlobalSearchResultItem;
	isMobile: boolean;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const KnowledgeItemItem = (
	props: KnowledgeItemItemProps
): ReactElement => {
	const {
		item,
		isMobile,
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const {
		knowledgeItem,
		hitHighlights
	} = item;

	const {
		title,
		media,
		author,
		category,
		descriptionPreview,
		viewsCount,
		lastModifiedDate
	} = knowledgeItem;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="knowledge-item-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<ItemLayout.FontIcon
					icon={hubItemKindToIcon[HubItemKind.KnowledgeItem]}
				/>
			</ItemLayout.IconContainer>

			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="title"
					title={title}
				>
					<HighlightedText
						text={title}
						highlight={highlights.get("knowledgeItem.title")}
					/>
				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.InfoIconWrapper>
						<Icon value="pen" size="small" />
					</ItemLayout.InfoIconWrapper>
					<span
						{...setTrackingId("last-modified-date")}
					>
						<DateString
							format="RelativeTimestamp"
							value={lastModifiedDate}
							timezone={timezone}
						/>
					</span>
					{author && (
						<>
							<PipeSeparator />
							{localize("_(Edited)By")}
							<Space />
							<span
								{...setTrackingId("author")}
							>
								{author.name}
							</span>
						</>
					)}
					{category && (
						<span
							{...setTrackingId("category")}
						>
							<PipeSeparator />
							{category.name}
						</span>
					)}
				</ItemLayout.Info>
				<ItemLayout.Content>
					<HighlightedText
						text={descriptionPreview}
						highlight={highlights.get("knowledgeItem.descriptionPreview")}
					/>
				</ItemLayout.Content>
				<ItemLayout.Statistics>
					<IconAndText
						text={statisticsFormatter(viewsCount)}
					>
						<IconAndText.Icon
							value="eye"
							size="small"
						/>
					</IconAndText>
				</ItemLayout.Statistics>
			</ItemLayout.InfoWrapper>
			{!isMobile && (
				<ItemLayout.Thumbnail>
					<HubThumbnail
						hubItemKind={HubItemKind.KnowledgeItem}
						media={media}
						title={title}
						mediaPlaceholderSize="medium"
					/>
				</ItemLayout.Thumbnail>
			)}
		</ItemLayout>
	);
};

KnowledgeItemItem.displayName = "KnowledgeItemItem";
